import React, { useEffect, useState, Component } from 'react';
import { FlameIcon, MoonIcon, HeartIcon, BookOpenIcon, HeartHandshakeIcon, UsersIcon, ListIcon, AlertCircleIcon, MapPinIcon, UserIcon, LinkIcon, ZapIcon, BookmarkIcon, GlobeIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { FilterSidebar, FilterSidebarWrapper, NavItem, FilterSection } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useDarkMode } from '../context/DarkModeContext';
const spiceLevels = [{
  name: 'Mild',
  level: 1,
  count: 125,
  description: 'Sweet kisses and cuddles. No on-page intimacy.',
  examples: ['Closed Door', 'Sweet Romance', 'Light Kissing']
}, {
  name: 'Warm',
  level: 2,
  count: 97,
  description: 'Flirting, some heat, and fade-to-black scenes.',
  examples: ['Open Door', 'Tension', 'Makeout Scenes']
}, {
  name: 'Spicy',
  level: 3,
  count: 83,
  description: 'On-page steamy scenes and descriptive romance.',
  examples: ['One Bed', 'High Heat', 'Dirty Talk']
}, {
  name: 'Extra Hot',
  level: 4,
  count: 59,
  description: 'Multiple explicit scenes with kink or taboo.',
  examples: ['BDSM', 'Praise/Degradation', 'Multiple Partners']
}, {
  name: 'Inferno',
  level: 5,
  count: 32,
  description: 'Highly explicit content with little to no plot focus. Intense, graphic, and unapologetically sexual.',
  examples: ['Erotica', 'Taboo', 'Explicit']
}];
const darknessLevels = [{
  name: 'Hint of Shadow',
  level: 1,
  count: 0,
  description: 'Low-level emotional tension or light trauma backstories. The book remains hopeful in tone and avoids graphic descriptions.',
  examples: {
    themes: ['grief', 'mild trauma', 'emotional abuse'],
    authors: ['Abby Jimenez', 'Ali Hazelwood']
  }
}, {
  name: 'Into the Grey',
  level: 2,
  count: 0,
  description: 'Moderate emotional weight or trauma explored through flashbacks or subplots. May include mental health themes, addiction, or loss.',
  examples: {
    themes: ['addiction', 'mental illness', 'parental neglect'],
    authors: ['Talia Hibbert', 'Kristen Ashley']
  }
}, {
  name: 'Embrace the Abyss',
  level: 3,
  count: 0,
  description: 'Strong, present-day dark content with moderate graphic scenes. Deals with themes like abduction, trafficking, or revenge with emotional fallout.',
  examples: {
    themes: ['abduction', 'trafficking', 'revenge'],
    authors: ['Lana Pecherczyk', 'Shantel Tessier']
  }
}, {
  name: 'Pitch Black',
  level: 4,
  count: 0,
  description: 'Heavy psychological or physical darkness, including captivity, dub-con, and deep manipulation. For readers who actively seek dark thrill elements.',
  examples: {
    themes: ['dub-con', 'captivity', 'manipulation'],
    authors: ['J.M. Darhower', 'K. Webster']
  }
}, {
  name: 'The Void',
  level: 5,
  count: 0,
  description: 'Extremely graphic, taboo, or triggering content. Includes non-con, torture, intense psychological abuse, and themes not suitable for all audiences.',
  examples: {
    themes: ['non-con', 'torture', 'taboo'],
    authors: ['Miranda Grant', 'Cora Reilly']
  }
}];
const SpiceDarkness: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    darkMode
  } = useDarkMode();
  // Scroll to section when hash changes (including on first load)
  useEffect(() => {
    const hash = location.hash;
    if (hash === '#spice' || hash === '#darkness') {
      // Small delay to ensure DOM is rendered
      const timer = setTimeout(() => {
        const el = document.getElementById(hash.replace('#', ''));
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Pairing Types',
    href: '/pairing-types',
    icon: LinkIcon,
    subItems: [{
      label: 'Monogamous',
      href: '/pairing-types?category=monogamous'
    }, {
      label: 'Why Choose',
      href: '/pairing-types?category=why-choose'
    }, {
      label: 'Closed Triad',
      href: '/pairing-types?category=closed-triad'
    }, {
      label: 'Polyamorous',
      href: '/pairing-types?category=polyamorous'
    }, {
      label: 'NB / Trans Inclusive',
      href: '/pairing-types?category=nb-trans'
    }, {
      label: 'No Romance',
      href: '/pairing-types?category=no-romance'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    isActive: true,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Burn Levels',
    href: '/burn-levels',
    icon: ZapIcon
  }, {
    label: 'Ending Types',
    href: '/ending-types',
    icon: BookmarkIcon
  }, {
    label: 'Representation',
    href: '/representation',
    icon: UsersIcon,
    subItems: [{
      label: 'Sexual Orientation & Gender',
      href: '/representation?category=Sexual+Orientation+%26+Gender'
    }, {
      label: 'Race & Ethnicity',
      href: '/representation?category=Race+%26+Ethnicity'
    }, {
      label: 'Neurodiversity & Disability',
      href: '/representation?category=Neurodiversity+%26+Disability'
    }, {
      label: 'Mental Health',
      href: '/representation?category=Mental+Health'
    }]
  }, {
    label: 'Cultural Identity',
    href: '/cultural-identity',
    icon: GlobeIcon
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon,
    subItems: [{
      label: 'Personal Growth',
      href: '/themes?category=Personal+Growth'
    }, {
      label: 'Destiny',
      href: '/themes?category=Destiny'
    }, {
      label: 'Power',
      href: '/themes?category=Power'
    }, {
      label: 'Morality',
      href: '/themes?category=Morality'
    }, {
      label: 'Relationships',
      href: '/themes?category=Relationships'
    }, {
      label: 'Identity',
      href: '/themes?category=Identity'
    }]
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon
  }];
  return <div className={dk.page} data-id="element-14496" data-mp="3q59zg23qsqjk">
      <ContentPageHeader title="Spice & Darkness" icon={getContentPageIcon('spicedarkness')} searchPlaceholder="Search spice & darkness levels..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-14497" data-mp="3l91s3avse1my" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-14498" data-mp="i1mgq5t13tgs">
        <div className="flex gap-8" data-id="element-14499" data-mp="d4ciq1rkq9hc">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-14500" data-mp="d3b81yegzp05">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={[]} onReset={() => {}} searchable={false} data-id="element-14501" data-mp="3vhtynjughgmw" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0 space-y-12" data-id="element-14502" data-mp="3licrsz3gwwpc">
            {/* Spice Section — id matches #spice hash */}
            <section id="spice" data-id="element-14503" data-mp="3hikrf9o89wv2">
              <div className="text-center mb-8" data-id="element-14504" data-mp="1x00inullo1o7">
                <h1 className={`text-2xl sm:text-3xl font-bold flex justify-center items-center gap-2 ${dk.textHeading}`} data-id="element-14505" data-mp="1wz8kqd4j5rur">
                  <FlameIcon className="h-6 w-6 text-red-500" data-id="element-14506" data-mp="260ycxl68kutl" />
                  Spice Levels
                </h1>
                <p className={`text-base sm:text-lg max-w-2xl mx-auto mt-3 ${dk.textSecondary}`} data-id="element-14507" data-mp="2b0b76qzft467">
                  Find books that match your preferred level of romantic
                  content.
                </p>
              </div>
              <div className="space-y-4 sm:space-y-6" data-id="element-14508" data-mp="1na77gupddokh">
                {spiceLevels.map(level => <div key={level.name} className={`rounded-lg shadow-md p-4 sm:p-6 ${dk.card}`} data-id="element-14509" data-mp="1s0xcg6tvvpen">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4" data-id="element-14510" data-mp="ul08jum06wyp">
                      <h2 className={`text-lg sm:text-xl font-semibold flex items-center ${dk.textHeading}`} data-id="element-14511" data-mp="zi9wt6xnp2ky">
                        <div className="flex space-x-1 mr-3" data-id="element-14512" data-mp="nhjy7cq8nzi0">
                          {[...Array(level.level)].map((_, i) => <FlameIcon key={i} className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" fill="currentColor" data-id="element-14513" data-mp="11buer6s30ncz" data-mpi={i} />)}
                        </div>
                        {level.name}
                      </h2>
                      <Link to={`/tagged-books?tag=${encodeURIComponent(level.name)}&category=spice`} className={`inline-flex items-center px-3 py-1 ${rt.badgeRadius} text-sm font-medium ${dk.badgeNeutral} hover:${dk.surfaceMuted} transition-colors`} data-id="element-14514" data-mp="ika0zawlyjjm">
                        {level.count} books
                      </Link>
                    </div>
                    <p className={`text-sm sm:text-base mb-3 sm:mb-4 ${dk.textSecondary}`} data-id="element-14515" data-mp="36ve3ve3tkkfd">
                      {level.description}
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-14516" data-mp="2xxu1u2y4uk3p">
                      {level.examples.map((example, index) => <Link key={index} to={`/tagged-books?tag=${encodeURIComponent(example)}&category=spice&level=${encodeURIComponent(level.name)}`} className={`inline-flex items-center px-2 sm:px-3 py-1 ${rt.badgeRadius} text-xs sm:text-sm ${darkMode ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' : 'bg-red-50 text-red-700 hover:bg-red-100'} transition-colors`} data-id="element-14517" data-mp="32x6x9c32kzhw" data-mpi={index}>
                          {example}
                        </Link>)}
                    </div>
                  </div>)}
              </div>
            </section>

            {/* Darkness Section — id matches #darkness hash */}
            <section id="darkness" className={`pt-8 border-t ${dk.border}`} data-id="element-14518" data-mp="2kpbwn1ejxes7">
              <div className="text-center mb-8" data-id="element-14519" data-mp="2gnqd4x7ii291">
                <h1 className={`text-2xl sm:text-3xl font-bold flex justify-center items-center gap-2 ${dk.textHeading}`} data-id="element-14520" data-mp="2gojmm5xuv5uv">
                  <MoonIcon className={`h-6 w-6 ${dk.textPrimary}`} data-id="element-14521" data-mp="38fpgi4mzg7bp" />
                  Dark Romance Levels
                </h1>
                <p className={`text-base sm:text-lg max-w-2xl mx-auto mt-3 ${dk.textSecondary}`} data-id="element-14522" data-mp="34evrf7xt0mh8">
                  Explore dark romance by intensity, from morally grey to pitch
                  black.
                </p>
              </div>
              <div className="space-y-4 sm:space-y-6" data-id="element-14523" data-mp="3s3oo56m92ycw">
                {darknessLevels.map(level => <div key={level.name} className={`rounded-lg shadow-md p-4 sm:p-6 ${dk.card}`} data-id="element-14524" data-mp="3nc6nkjo52zsg">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4" data-id="element-14525" data-mp="3upxvr14xwggn">
                      <h2 className={`text-lg sm:text-xl font-semibold flex items-center ${dk.textHeading}`} data-id="element-14526" data-mp="8fv92g6az4vf">
                        <div className="flex space-x-1 mr-3" data-id="element-14527" data-mp="cfvp2591vo4l">
                          {[...Array(level.level)].map((_, i) => <MoonIcon key={i} className={`h-4 w-4 sm:h-5 sm:w-5 ${dk.textPrimary}`} fill="currentColor" data-id="element-14528" data-mp="mfwbw164omdj" data-mpi={i} />)}
                        </div>
                        {level.name}
                      </h2>
                      <Link to={`/tagged-books?tag=${encodeURIComponent(level.name)}&category=darkness`} className={`inline-flex items-center px-3 py-1 ${rt.badgeRadius} text-sm font-medium ${dk.badgeNeutral} hover:${dk.surfaceMuted} transition-colors`} data-id="element-14529" data-mp="109dr59rs7cxr">
                        {level.count} books
                      </Link>
                    </div>
                    <p className={`text-sm sm:text-base mb-3 sm:mb-4 ${dk.textSecondary}`} data-id="element-14530" data-mp="368vc9nafs72j">
                      {level.description}
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-14531" data-mp="3fyomokyueg57">
                      {level.examples.themes.map((theme, index) => <Link key={`theme-${index}`} to={`/tagged-books?tag=${encodeURIComponent(theme)}&category=darkness&level=${encodeURIComponent(level.name)}`} className={`inline-flex items-center px-2 sm:px-3 py-1 ${rt.badgeRadius} text-xs sm:text-sm ${darkMode ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' : 'bg-red-50 text-red-700 hover:bg-red-100'} transition-colors`} data-id="element-14532" data-mp="3typpi5yo3xnb" data-mpi={index}>
                          {theme}
                        </Link>)}
                      {level.examples.authors.map((author, index) => <Link key={`author-${index}`} to={`/tagged-books?tag=${encodeURIComponent(author)}&category=darkness&level=${encodeURIComponent(level.name)}&type=author`} className={`inline-flex items-center px-2 sm:px-3 py-1 ${rt.badgeRadius} text-xs sm:text-sm ${darkMode ? 'bg-indigo-900/30 text-indigo-300 hover:bg-indigo-900/50' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'} transition-colors`} data-id="element-14533" data-mp="18wlm11grvsc" data-mpi={index}>
                          {author}
                        </Link>)}
                    </div>
                  </div>)}
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
};
export default SpiceDarkness;